import { handleSaveDraftActivity, handleCreateActivity, handleUpdateDraftActivity } from '@/services/activities'
import { useAppDispatch, useAppSelector } from '@/store'
import { resetActivityData, setActivityFormData } from '@/store/slices/activitySlice'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { ACTIVITY_TYPE, ACTIVITY_TYPES_ENUM, OrderType } from '@/types/Activity'
import { getDateFromISOString, getFormattedDateString, getUUID } from '@/utils/commons'
import { ROUTES } from '@/utils/constants/routesConstants'
import { useState } from 'react'
import {useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { COUNTRY_CODES_FREQUENCY, APPLICATION_ID } from '@/utils/constants/globalConstants'

export default function useActivityReview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isCancel, setIsCancel] = useState(false)
  const activityFormData = useSelector(state => state.activity.formData)

  const getActivityEnum = activityType => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.WELCOME
    }
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return ACTIVITY_TYPES_ENUM.LOGIN
    }
    if (activityType === ACTIVITY_TYPE.PLAY_GAME) {
      return ACTIVITY_TYPES_ENUM.PLAY_GAME
    }
    if (activityType === ACTIVITY_TYPE.WATCH_VIDEO) {
      return ACTIVITY_TYPES_ENUM.WATCH_VIDEO
    }
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return ACTIVITY_TYPES_ENUM.SURVEY
    }
    if (activityType === ACTIVITY_TYPE.POINTS_TRANSFER) {
      return ACTIVITY_TYPES_ENUM.POINTS_TRANSFER
    }
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY
    }
    if (activityType === ACTIVITY_TYPE.REDEEM_POINTS) {
      return ACTIVITY_TYPES_ENUM.REDEEM_POINTS
    }
    if (activityType === ACTIVITY_TYPE.GENERIC_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY
    }
    return null
  }

  const getDisplayActivityType = activityType => {
    switch(activityType){
      case ACTIVITY_TYPES_ENUM.WELCOME:  return ACTIVITY_TYPE.WELCOME_ACTIVITY
      case ACTIVITY_TYPES_ENUM.LOGIN: return ACTIVITY_TYPE.LOGIN
      case ACTIVITY_TYPES_ENUM.PLAY_GAME: return ACTIVITY_TYPE.PLAY_GAME
      case ACTIVITY_TYPES_ENUM.WATCH_VIDEO: return ACTIVITY_TYPE.WATCH_VIDEO
      case ACTIVITY_TYPES_ENUM.SURVEY: return ACTIVITY_TYPE.SURVEY
      case ACTIVITY_TYPES_ENUM.POINTS_TRANSFER: return ACTIVITY_TYPE.POINTS_TRANSFER
      case ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY: return ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY
      case ACTIVITY_TYPES_ENUM.REDEEM_POINTS: return ACTIVITY_TYPE.REDEEM_POINTS
      case ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY: return ACTIVITY_TYPE.GENERIC_ACTIVITY
      default: return activityType
    }
    
  }
  const getSlug = activityType => {
    return `reward-points::${activityType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const countryCode = useAppSelector(state => state.countryCode).countryCode

  const handlePublish = async () => {
    // these are the required fields for Login & Welcome API call
    const param = {
      applicationId: APPLICATION_ID[countryCode] || 1,
      title: '',
      activityType: getActivityEnum(activityFormData?.activityType), // WELCOME | LOGIN | POINTS_ON_ORDER_DELIVERY
      tags: '',
      slug: getSlug(activityFormData?.activityType), //common
      pointsEarned: activityFormData?.pointsEarned, // common
      maxLimitPerUser: 0,
      dailyLoginLimit: activityFormData?.dailyLoginLimit, // Login
      conversionRate: 0,
      amountPerUnit: 0,
      pointsAwardedPerUnit: 0,
      ...(COUNTRY_CODES_FREQUENCY.includes(countryCode) && { frequency: activityFormData?.frequency }),
      activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
      activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
      allTimeLoginLimit: activityFormData?.allTimeLoginLimit, // Login
      activityName: activityFormData?.activityName,
      orderType: OrderType[activityFormData?.orderType],
      localCurrency: activityFormData?.localCurrency,
      ...(COUNTRY_CODES_FREQUENCY.includes(countryCode)
        ? { numberOfCases: activityFormData?.numberOfCases }
        : { localCurrency: activityFormData?.localCurrency }
      ),
      hasEndDate: activityFormData?.hasEndDate,
      receivePoints: activityFormData?.receivePoints,
    }

    const formBasedParams = Object.fromEntries(
      Object.entries(param).filter(([key, value]) => {
        const isFromFormData =
          key in activityFormData ||
          value === getActivityEnum(activityFormData?.activityType) ||
          value === getSlug(activityFormData?.activityType) ||
          value === getFormattedDateString(activityFormData?.startDate) ||
          value === getFormattedDateString(activityFormData?.endDate) ||
          value === OrderType[activityFormData?.orderType]

        const isManuallyIncluded = ['tags', 'applicationId', 'slug'].includes(key)
        if (isManuallyIncluded) {
          return true
        }

        return isFromFormData && value !== undefined && value !== null && value !== ''
      })
    )

    try {
      const response = await handleCreateActivity({ data: formBasedParams })
      if (response?.success) {
        dispatch(setActivityFormData(activityFormData))
        dispatch(setToastNotifications({ show: true, message: 'New Activity published', type: 'success' }))
        handleResetFormData()
        navigate(ROUTES.SALES_TARGETS + '?tab=0')
      } else {
        const errMsg = response?.errorMessage || 'An unknown error occurred'
        dispatch(setToastNotifications({ show: true, message: errMsg, type: 'error' }))
      }
    } catch (error) {
      const errMsg = error?.response?.data?.errorMessage || error?.message || 'An unknown error occurred'
      dispatch(setToastNotifications({ show: true, message: errMsg, type: 'error' }))
    }
  }

  const handleDraft = async () => {
    const isNameChanged = activityFormData?.activityName !== activityFormData?.name
    const callPutApi = activityFormData?.status?.toLowerCase()=='draft' && !isNameChanged
    let response;
    if(!callPutApi){
      response = await handleSaveDraftActivity({
      data: {
        ...activityFormData,
        activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
        activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
        activityType: getActivityEnum(activityFormData?.activityType),
        status: 'DRAFT',
        uuid: crypto.randomUUID(),
      },
    })}else{
      response = await handleUpdateDraftActivity({
        data: {
          ...activityFormData,
          activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
          activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
          activityType: getActivityEnum(activityFormData?.activityType),
          status: 'DRAFT',
          uuid: isNameChanged? getUUID(activityFormData?.activityName):  getUUID(activityFormData?.name)
        }
      })
    }
    
    if (response?.success) {
      dispatch(setToastNotifications({ show: true, message: (callPutApi? 'Activity draft updated' : 'New Activity draft'), type: 'success' }))
      handleResetFormData()
      navigate(ROUTES.SALES_TARGETS + '?tab=0')
    } else {
      dispatch(setToastNotifications({ show: true, message: 'Failed to save draft', type: 'error' }))
    }
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.SALES_TARGETS + '?tab=0')
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

   

  const getFormDataWithOrder = (formDataFromApi) => {
    let {
      activityType,
      pointsEarned,
      hasEndDate,
      activityStartDate: startDate,
      activityEndDate: endDate,
      frequency,
      dailyLoginLimit,
      allTimeLoginLimit,
      activityName,
      activityName: name,
      orderType,
      localCurrency,
      numberOfCases,
      receivePoints,
      status
    } = formDataFromApi
    if(status=='Published'){
      startDate = formDataFromApi?.activeStartDate || formDataFromApi?.activityStartDate
      endDate = formDataFromApi?.activeEndDate || formDataFromApi?.activityEndDate
    }
    if(pointsEarned){
      pointsEarned = String(pointsEarned)
    }
    if(numberOfCases){
      numberOfCases = String(numberOfCases)
    }
    if(receivePoints){
      receivePoints = String(receivePoints)
    }
    startDate = getDateFromISOString(startDate)
    if(endDate)
      endDate = getDateFromISOString(endDate)
    activityType = getDisplayActivityType(activityType)
    const isCountryCodeFrequency = COUNTRY_CODES_FREQUENCY.includes(countryCode)
     // Order alignment for Welcome review page
     const welcomeActivityOrder = {
      activityType: activityType,
      pointsEarned: pointsEarned,
      hasEndDate: hasEndDate,
      startDate: startDate,
      name: name,
      ...(hasEndDate && { endDate: endDate }),
    }
  
    // Order alignment for Login review page
    const loginActivityOrder = {
      activityType: activityType,
      ...(frequency && { frequency: frequency }),
      pointsEarned: pointsEarned,
      ...(dailyLoginLimit && { dailyLoginLimit: dailyLoginLimit }),
      ...(allTimeLoginLimit && { allTimeLoginLimit: allTimeLoginLimit }),
      startDate: startDate,
      endDate: endDate,
      receivePoints: receivePoints,
      name: name,
    }

    const pointsOnOrderDeliveryActivityOrder = () => {
      const fieldsArranged = {
        activityType: activityType,
        activityName: activityName,
        orderType: orderType,
        name: name,
      }
      if (isCountryCodeFrequency) {
        fieldsArranged.numberOfCases = numberOfCases
          fieldsArranged.pointsEarned = pointsEarned
      }
      else {
        fieldsArranged.localCurrency = localCurrency
          fieldsArranged.pointsEarned = pointsEarned
      }
      fieldsArranged.startDate = startDate
      fieldsArranged.endDate = endDate
      return fieldsArranged
    }

    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return welcomeActivityOrder
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return loginActivityOrder
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return pointsOnOrderDeliveryActivityOrder()
    }else{
      return {}
    }
  }
  
  

  return {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    getFormDataWithOrder
  }
}
